import { CLOSED_REASON_NOTE_MAX_LENGTH, ClosedReason, ErrorKey } from "@inrev/common";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { TextAreaInput } from "@portal/components/form/inputs/TextAreaInput";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button } from "@portal/components/ui/Button";
import { FC, useCallback, useMemo, useState } from "react";
import { errorMessageMap, isApiError } from "../../../utils/error";

export type ClosingAction = "close" | "reject";

type EnforceAllKeysPresent<Keys extends string, T extends { value: Keys }[]> = {
	[K in Keys]: T extends ArrayLike<{ value: K }> ? unknown : never;
}[Keys] extends never
	? T
	: never;

export type ClosedReasonModalProps = {
	closeFunction: ({
		closedReason,
		note,
		onSuccess,
	}: {
		closedReason: ClosedReason;
		note?: string;
		onSuccess: () => void;
	}) => void;
	isClosing: boolean;
	closeFunctionError: Error | null;
	onDismiss: () => void;
	onSuccess?: () => void;
	isAccount?: boolean;
	closingAction: ClosingAction;
};

const buildClosedReasonOptions = (
	isAccount: boolean,
): EnforceAllKeysPresent<ClosedReason, { value: ClosedReason; label: string }[]> => [
	{
		value: ClosedReason.lost_account,
		label: "Lost Account",
	},
	{
		value: ClosedReason.not_needed,
		label: isAccount ? "Capacity Not Needed" : "Bond Request Not Needed",
	},
	{
		value: ClosedReason.placed_elsewhere,
		label: "Placed Elsewhere",
	},
	{
		value: ClosedReason.stale,
		label: "Stale",
	},
	{
		value: ClosedReason.declined,
		label: "Declined",
	},
	{
		value: ClosedReason.other,
		label: "Other",
	},
];

export const ClosedReasonModal: FC<ClosedReasonModalProps> = ({
	closeFunction,
	isClosing,
	closeFunctionError,
	onDismiss,
	onSuccess,
	isAccount,
	closingAction,
}) => {
	const [closedReason, setClosedReason] = useState<ClosedReason | null>(null);
	const [note, setNote] = useState("");
	const isOther = closedReason === ClosedReason.other;
	const canSubmit = !!closedReason && (!isOther || !!note.trim());
	const closedReasonOptions = useMemo(() => buildClosedReasonOptions(!!isAccount), [isAccount]);

	const handleSubmit = () => {
		if (canSubmit) {
			closeFunction({
				closedReason,
				note: isOther ? note.trim() : undefined,
				onSuccess: onSuccess ? onSuccess : onDismiss,
			});
		}
	};

	const getErrorMessage = useCallback(() => {
		if (!closeFunctionError) {
			return errorMessageMap.default.description;
		}

		return isApiError(closeFunctionError)
			? errorMessageMap[closeFunctionError?.key || ErrorKey.default].description
			: errorMessageMap.default.description;
	}, [closeFunctionError]);

	return (
		<Modal onClickOutside={onDismiss}>
			<ModalItemWithHeader
				header={`${closingAction === "close" ? "Close" : "Reject"} ${isAccount ? "Account" : "Bond Request"}`}
				onClose={onDismiss}
				className="h-full max-h-[70vh]"
			>
				<div className="w-full h-full text-sm">
					<div className="flex w-[400px] min-w-[400px] p-6 pt-4">
						<div className="relative flex flex-1 flex-col space-y-2">
							{closeFunctionError && (
								<div data-testid="close-reason-error" className="text-red-600">
									{getErrorMessage()}
								</div>
							)}
							<div data-testid="close-reason-dropdown-label">
								Reason for {closingAction === "close" ? "closing" : "rejecting"}{" "}
								{isAccount ? "account" : "bond request"}:
							</div>
							<DropdownInput
								testId="closed-reason-dropdown"
								value={closedReason || undefined}
								placeholder="Reason"
								options={closedReasonOptions}
								onChange={(value) => {
									setClosedReason(value);
									if (value !== ClosedReason.other) setNote("");
								}}
								buttonClassName="text-sm"
								optionClassName="text-sm"
							/>
							{isOther && (
								<TextAreaInput
									data-testid="closed-reason-note-textarea"
									placeholder="Please describe the reason"
									className="h-[100px]"
									maxLength={CLOSED_REASON_NOTE_MAX_LENGTH}
									value={note}
									onChange={(e) => setNote(e.target.value ?? "")}
								/>
							)}
							<div className="flex flex-1 items-end justify-center space-x-2 mt-auto">
								<Button testId="close-reason-cancel-button" color="secondary" onClick={onDismiss}>
									Cancel
								</Button>
								<Button
									testId="close-reason-confirm-button"
									color="primary"
									filled
									disabled={!canSubmit || isClosing}
									onClick={handleSubmit}
								>
									Confirm
								</Button>
							</div>
						</div>
					</div>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
